import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, TriangleAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Field, Input, Select } from "@/components/Ui";

const Row = ({ label, value, ok }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[#EEF2F6] py-2 last:border-0">
    <span className="text-xs uppercase tracking-[0.1em] text-[#667788]">{label}</span>
    <span className={`text-sm font-semibold ${ok === false ? "text-[#B42318]" : ok ? "text-[#13795B]" : "text-[#102A43]"}`}>
      {value ?? "—"}
    </span>
  </div>
);

export default function AdminPayments() {
  const [health, setHealth] = useState(null);
  const [f, setF] = useState({ secret_key: "", webhook_secret: "", mode: "live" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/stripe-health").then(({ data }) => setHealth(data)).catch(() => setHealth(false));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!f.secret_key && !f.webhook_secret) return toast.error("Enter at least one value");
    setBusy(true);
    try {
      const { data } = await api.put("/admin/stripe-credentials", {
        secret_key: f.secret_key || null,
        webhook_secret: f.webhook_secret || null,
        mode: f.mode,
      });
      if (data.verified?.error) toast.error(`Saved, but Stripe rejected the key: ${data.verified.error}`);
      else toast.success(`Connected to ${data.verified?.account_id} (${data.verified?.country})`);
      setF({ secret_key: "", webhook_secret: "", mode: f.mode });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const live = health?.mode === "live" && health?.charges_enabled && !health?.stripe_error;

  return (
    <div data-testid="admin-payments">
      <Card>
        <div className="flex items-start gap-3">
          {live ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#13795B]" />
                : <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#B7791F]" />}
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Stripe connection
            </h2>
            <p className="mt-1 text-sm text-[#667788]" data-testid="stripe-state-line">
              {live ? "Live and accepting card payments."
                    : "Not ready to take live payments — check the details below."}
            </p>
          </div>
        </div>

        {health === false && <p className="mt-4 text-sm text-[#B42318]">Could not read the Stripe status.</p>}
        {health && (
          <div className="mt-5" data-testid="stripe-health-panel">
            <Row label="Mode" value={health.mode} ok={health.mode === "live"} />
            <Row label="Credentials from" value={health.credentials_source} />
            <Row label="Secret key" value={health.api_key_prefix ? `${health.api_key_prefix}…${health.api_key_last4}` : "not set"}
              ok={health.api_key_prefix === "sk_live_"} />
            <Row label="Account" value={health.account_id} />
            <Row label="Charges enabled" value={String(health.charges_enabled ?? "—")} ok={health.charges_enabled} />
            <Row label="Payouts enabled" value={String(health.payouts_enabled ?? "—")} ok={health.payouts_enabled} />
            <Row label="Webhook secret" value={health.webhook_secret_set ? `${health.webhook_secret_prefix}…${health.webhook_secret_last4} (${health.webhook_secret_length} chars)` : "not set"}
              ok={health.webhook_secret_set} />
            <Row label="Webhook secret from" value={health.webhook_secret_source} />
            <Row label="Webhook endpoints" value={(health.endpoints || []).map((e) => e.url).join(", ") || "none"} />
            <Row label="Prices" value={(health.prices || []).map((p) => `${p.lookup_key} ${p.currency.toUpperCase()} ${p.amount}`).join(" · ") || "none"} />
            {health.stripe_error && <Row label="Stripe error" value={health.stripe_error} ok={false} />}
            {health.updated_at && <Row label="Last updated" value={`${health.updated_at} by ${health.updated_by}`} />}
          </div>
        )}
        <Btn variant="ghost" data-testid="refresh-stripe-health" className="mt-4" onClick={load}>
          Refresh
        </Btn>
      </Card>

      <Card className="mt-5">
        <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
          Update Stripe credentials
        </h2>
        <p className="mt-1 text-sm text-[#667788]">
          Paste the keys from your own Stripe dashboard. They are stored on the platform and take
          effect immediately — no redeploy needed. Leave a field blank to keep the current value.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field label="Secret key" hint="Stripe → Developers → API keys → Secret key (sk_live_…)">
            <Input data-testid="stripe-secret-key" type="password" autoComplete="off"
              value={f.secret_key} onChange={(e) => setF({ ...f, secret_key: e.target.value })}
              placeholder="sk_live_…" />
          </Field>
          <Field label="Webhook signing secret" hint="Stripe → Developers → Webhooks → your endpoint → Reveal">
            <Input data-testid="stripe-webhook-secret" type="password" autoComplete="off"
              value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })}
              placeholder="whsec_…" />
          </Field>
          <Field label="Mode">
            <Select data-testid="stripe-mode" value={f.mode}
              onChange={(e) => setF({ ...f, mode: e.target.value })}
              options={[["live", "Live — real payments"], ["test", "Test — test cards only"]]} />
          </Field>
        </div>
        <Btn data-testid="save-stripe-credentials" className="mt-5" loading={busy} onClick={save}>
          Save and test connection
        </Btn>
        <p className="mt-3 text-xs text-[#8598ac]">
          Only platform administrators can see or change this. Keys are never shown again after saving —
          only their last four characters.
        </p>
      </Card>
    </div>
  );
}
